import { Controller } from "@hotwired/stimulus"

// § 3.5 Tier 2 Step 19 — live status refresh on the BIM index.
//
// Polls /projects/:id/bim.json every ~5s while any visible row is
// pending or inprogress. Stops polling when the server reports
// `polling: false` (every row settled). Status cells are re-rendered
// in place; we don't re-render the row body to keep the kebab popover
// + hover state stable.
export default class extends Controller {
  static targets = ["rows", "status"]
  static values = {
    statusUrl:    String,
    pollInterval: { type: Number, default: 5000 }
  }

  connect() {
    if (!this.hasStatusUrlValue) return
    if (this._anyRowTranslating()) {
      this._scheduleNext()
    }
  }

  disconnect() {
    this._clearTimer()
  }

  async _refresh() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) return
      const json = await response.json().catch(() => null)
      if (!json) return
      this._applyRows(Array.isArray(json.bim_models) ? json.bim_models : [])
      if (json.polling) {
        this._scheduleNext()
      }
    } catch (_err) {
      // Transient network error — retry on the next tick rather than
      // burn a notification budget on a flaky polling endpoint.
      this._scheduleNext()
    }
  }

  _applyRows(rows) {
    rows.forEach(row => {
      const tr = this._findRow(row.id)
      if (!tr) return
      const cell = tr.querySelector("[data-bim-models-index-target='status']")
      if (cell) cell.innerHTML = this._renderBadge(row)
      // Refresh thumbnail when a row first goes Ready — index page
      // serves the empty placeholder until the poller writes the URL.
      if (row.thumbnail_url && row.manifest_status === "success") {
        const img = tr.querySelector("img")
        if (!img) {
          const wrap = tr.querySelector(".bg-gray-100")
          if (wrap) {
            wrap.innerHTML = `<img src="${this._escape(row.thumbnail_url)}" alt="" class="w-full h-full object-cover" loading="lazy">`
          }
        }
      }
    })

    // Reload the page entirely if a row's terminal state means the
    // table needs to re-render (e.g. we don't try to replace the
    // failure-reason tooltip in place — full reload is honest).
    const stillTranslating = rows.some(r => r.manifest_status === "pending" || r.manifest_status === "inprogress")
    if (!stillTranslating) {
      // Soft refresh: a full reload re-renders the row + the
      // superseded toggle counter. Cheap, and the user is on the
      // same URL so no scroll loss.
      window.location.reload()
    }
  }

  _renderBadge(row) {
    switch (row.manifest_status) {
      case "success":
        return `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[12px] font-medium">Ready</span>`
      case "inprogress":
      case "pending":
        return `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[12px] font-medium">${this._escape(row.display_status)}</span>`
      case "failed":
        return `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[12px] font-medium" title="${this._escape(row.translation_failure_reason || "")}">Failed</span>`
      case "timeout":
        return `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[12px] font-medium">Timed out</span>`
      default:
        return this._escape(row.display_status || row.manifest_status)
    }
  }

  _findRow(id) {
    const root = this.hasRowsTarget ? this.rowsTarget : this.element
    return root.querySelector(`[data-bim-models-index-row-id="${id}"]`)
  }

  _anyRowTranslating() {
    if (!this.hasRowsTarget) return false
    return Array.from(this.rowsTarget.querySelectorAll("[data-bim-models-index-row-id]")).some(tr => {
      const badge = tr.querySelector("[data-bim-models-index-target='status']")
      return badge && /Translating|Queued/i.test(badge.textContent || "")
    })
  }

  _scheduleNext() {
    this._clearTimer()
    this._timer = setTimeout(() => this._refresh(), this.pollIntervalValue)
  }

  _clearTimer() {
    if (this._timer) {
      clearTimeout(this._timer)
      this._timer = null
    }
  }

  _escape(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
